<template>
  <div>
    <div class="rfid-table">
      <div class="rfid-table-tools">
        <div class="rfid-tools-left">
          <input
              v-model="searchTerm"
              type="text"
              class="rfid-search-input"
              placeholder="Pesquisar patrimônio ou RFID..."
          />
        </div>
      </div>

      <div class="rfid-div-filter">
        <div class="side-by-side">
          <CheckboxSimple
              title="Com Tag RFID"
              v-model="showWithRfid"
          />
        </div>

        <div class="side-by-side rfid-div-check-sem-rfid">
          <CheckboxSimple
              title="Sem Tag RFID"
              v-model="showWithoutRfid"
          />
        </div>
      </div>

      <div class="rfid-table-header">
        <div class="col-pat">Nº Patrimônio</div>
        <div class="col-rfid">Nº Tag RFID</div>
      </div>

      <draggable
          :disabled="isDragDisabled"
          handle=".rfid-drag-handle"
          tag="div"
      >
        <div
            class="rfid-row"
            v-for="(pat, index) in dragList"
            :key="pat.id || pat.tempId || ('idx-' + index)"
            :class="{ 'rfid-row-duplicate': pat.hasDuplicateBarCode || pat.hasDuplicateRfid }"
        >
          <div class="col-pat">
            <div class="rfid-pat-wrap">
              <i class="fa-solid fa-grip-lines rfid-drag-handle"></i>

              <template v-if="!pat.editingBarCode">
                <span
                    @click="pat.editingBarCode = true"
                    class="pat-pill"
                >{{ pat.barCode }}</span>
              </template>

              <template v-else>
                <div class="input-edit-wrap">
                  <input
                      class="input-edit"
                      v-model="pat.barCode"
                      @keyup.enter="savePatEdit(pat, 'barCode')"
                  />
                  <i
                      class="fa-solid fa-check check-icon"
                      @click="savePatEdit(pat, 'barCode')"
                  ></i>
                </div>
              </template>

              <i
                  v-if="pat.isNew"
                  class="fa-solid fa-plus rfid-new-icon"
              ></i>
            </div>
          </div>

          <div class="col-rfid">
            <div class="rfid-rfid-wrap">
              <template v-if="!pat.editingRfid">
                <span
                    class="rfid-pill"
                    @click="pat.editingRfid = true"
                >
                  {{ pat.rfid || 'N/I' }}
                </span>
              </template>

              <template v-else>
                <div class="input-edit-wrap">
                  <input
                      class="input-edit"
                      v-model="pat.rfid"
                      placeholder="Informe o Nº RFID"
                      @keyup.enter="savePatEdit(pat, 'rfid')"
                  />
                  <i
                      class="fa-solid fa-check check-icon"
                      @click="savePatEdit(pat, 'rfid')"
                  ></i>
                </div>
              </template>

              <i
                  class="fa-solid fa-xmark rfid-remove-icon"
                  @click="removePatrimony(pat)"
              ></i>
            </div>
          </div>
        </div>
      </draggable>

      <div
          class="text-center"
          v-if="dragList.length === 0"
      >
        <br/>
        Nenhum registro encontrado!
      </div>
    </div>

    <div class="rfid-final-row">
      <Button
          _key="btnFinish"
          :disabled="dragList.length === 0 || hasDuplicateError"
          type="success"
          title="Vincular"
          classIcon="fa-solid fa-arrows-retweet"
          size="large"
          :clicked="onFinish"
      />
    </div>
  </div>
</template>

<script>
import CheckboxSimple from "@nixweb/nixloc-ui/src/component/forms/CheckboxSimple";
import Button from "@nixweb/nixloc-ui/src/component/forms/Button";
import draggable from "vuedraggable";

export default {
  name: "ProductRfidLinkerTable",
  components: {CheckboxSimple, Button, draggable},
  props: {
    selectedProduct: {
      type: Object,
      required: true,
    },
    operationMode: {
      type: String,
      default: "update",
    },
    hasDuplicateError: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchTerm: "",
      showWithRfid: true,
      showWithoutRfid: true,
    };
  },
  computed: {
    isDragDisabled() {
      return !this.selectedProduct;
    },
    filteredList() {
      if (!this.selectedProduct) return [];

      let list = this.selectedProduct.patrimonies || [];

      if (this.operationMode === "add" && !this.hasDuplicateError) {
        list = list.filter((p) => p.isNew);
      }

      if (this.showWithRfid && !this.showWithoutRfid) {
        list = list.filter((p) => !!p.rfid);
      } else if (!this.showWithRfid && this.showWithoutRfid) {
        list = list.filter((p) => !p.rfid);
      } else if (!this.showWithRfid && !this.showWithoutRfid) {
        list = [];
      }

      if (this.searchTerm) {
        const term = this.searchTerm.toLowerCase();
        list = list.filter((p) => {
          const bc = (p.barCode || "").toLowerCase();
          const rf = (p.rfid || "").toLowerCase();
          return bc.includes(term) || rf.includes(term);
        });
      }

      return list;
    },
    dragList: {
      get() {
        return this.filteredList;
      },
      set(newList) {
        const prod = this.selectedProduct;
        if (!prod) return;

        const filteredIds = newList.map((p) => p.id || p.tempId);
        const queue = newList.slice();
        const result = [];

        prod.patrimonies.forEach((item) => {
          const itemKey = item.id || item.tempId;
          if (filteredIds.indexOf(itemKey) !== -1) {
            result.push(queue.shift());
          } else {
            result.push(item);
          }
        });

        prod.patrimonies.splice(0, prod.patrimonies.length, ...result);
      },
    },
  },
  methods: {
    savePatEdit(pat, field) {
      pat[field] = (pat[field] || "").trim();
      if (field === "barCode") pat.editingBarCode = false;
      if (field === "rfid") pat.editingRfid = false;
    },
    removePatrimony(pat) {
      const prod = this.selectedProduct;
      if (!prod) return;
      const idx = prod.patrimonies.indexOf(pat);
      if (idx > -1) {
        prod.patrimonies.splice(idx, 1);
      }
    },
    scrollToTop() {
      this.$nextTick(() => {
        window.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      });
    },
    onFinish() {
      if (this.hasDuplicateError) this.scrollToTop();
      this.$emit("finish");
    },
  },
};
</script>

<style scoped>
.rfid-table {
  margin-top: 10px;
}

.rfid-table-tools {
  padding: 10px 18px 0 18px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.rfid-tools-left {
  flex: 1;
}

.rfid-search-input {
  width: 100%;
  padding: 5px 10px;
  font-size: 13px;
  border-radius: 50px;
  border: 1px solid #e5e4e8;
}

.rfid-div-filter {
  margin: 15px 10px 15px 25px;
}

.rfid-div-check-sem-rfid {
  margin-left: 10px;
}

.rfid-table-header {
  display: flex;
  padding: 12px 18px;
  background: #f9fafb;
  border-bottom: 1px solid #eef0f3;
  font-size: 14px;
  font-weight: 600;
  color: #6b7280;
}

.rfid-row {
  display: flex;
  padding: 14px 18px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
}

.rfid-row-duplicate {
  background: #fef2f2;
  border-left: 3px solid #ef4444;
}

.col-pat {
  min-width: 210px;
}

.col-rfid {
  flex: 1;
}

.rfid-pat-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.rfid-drag-handle {
  font-size: 14px;
  color: #9ca3af;
  cursor: grab;
}

.rfid-drag-handle:hover {
  color: #6b7280;
}

.pat-pill {
  background: #e0e7ff;
  color: #4338ca;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 13px;
  cursor: pointer;
}

.rfid-rfid-wrap {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.rfid-pill {
  background: #f3f4f6;
  color: #374151;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 13px;
  cursor: pointer;
}

.input-edit-wrap {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.input-edit {
  border: 1px solid #d1d5db;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 14px;
  min-width: 190px;
}

.check-icon {
  color: #059669;
  font-size: 16px;
  cursor: pointer;
}

.check-icon:hover {
  color: #047857;
}

.rfid-remove-icon {
  font-size: 14px;
  color: #f97373;
  cursor: pointer;
}

.rfid-remove-icon:hover {
  color: #dc2626;
}

.rfid-new-icon {
  font-size: 12px;
  color: #10b981;
  margin-right: 8px;
}

.rfid-final-row {
  padding: 20px;
  text-align: center;
}
</style>
